import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

// The API refuses a Patient Portal token that has no verified-email claim. A family holding a
// token issued before that claim existed would otherwise see every screen fail until it expired.
// Recovery, in order: one fresh token and a retry; then one trip through sign-in; then a message.

const UNAUTHORIZED = 401;
// The silent token call can hang for up to a minute; give up early and fall back to signing in.
export const FRESH_TOKEN_TIMEOUT_MS = 5000;
// At most one automatic sign-in per browser tab. It is never cleared by a later success: on a
// page where one request is refused and another succeeds, clearing it would loop sign-in forever.
export const SIGN_IN_RETRY_MARKER = "patientPortalSignInRetried";
const RETRIED_FLAG = "patientPortalTokenRetried";

interface RecoveryAuth0Client {
  getTokenSilently: (options: { ignoreCache: boolean }) => Promise<string>;
  getUser: () => Promise<{ email_verified?: boolean } | undefined>;
  loginWithRedirect: (options: { appState: { targetUrl: string } }) => Promise<void>;
  getIdTokenClaims: () => Promise<{ iss?: string } | undefined>;
}

export interface SessionRecoveryOptions {
  auth0Client: RecoveryAuth0Client;
  onUnrecoverable: () => void;
  storage?: Storage | null;
  getReturnTo?: () => string;
}

type RecoverableConfig = InternalAxiosRequestConfig & { [RETRIED_FLAG]?: boolean };

export const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Auth0 did not answer within ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const getBearerToken = (config?: InternalAxiosRequestConfig): string | undefined => {
  const header = config?.headers?.Authorization ?? config?.headers?.authorization;
  if (typeof header !== "string") return undefined;
  const match = header.match(/^Bearer (.+)$/);
  return match ? match[1] : undefined;
};

const getTokenIssuer = (token?: string): string | undefined => {
  const payload = token?.split(".")[1];
  if (!payload) return undefined;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const { iss } = JSON.parse(atob(padded));
    return typeof iss === "string" ? iss : undefined;
  } catch {
    return undefined;
  }
};

// Storage can be unavailable (private browsing, blocked site data); report failure so the
// caller never redirects without a working loop guard. Even reading the property can throw.
const getSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readMarker = (storage: Storage | null): string | null => {
  try {
    return storage ? storage.getItem(SIGN_IN_RETRY_MARKER) : null;
  } catch {
    return null;
  }
};

const writeMarker = (storage: Storage | null): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(SIGN_IN_RETRY_MARKER, "1");
    return storage.getItem(SIGN_IN_RETRY_MARKER) === "1";
  } catch {
    return false;
  }
};

// A family who signs out themselves gets a fresh allowance: at most one extra sign-in per
// sign-out they chose, so this can't loop on its own.
export const resetSignInRetry = (storage: Storage | null = getSessionStorage()) => {
  try {
    storage?.removeItem(SIGN_IN_RETRY_MARKER);
  } catch {
    // storage blocked: nothing to reset
  }
};

// Never send the family back to Auth0's one-time callback parameters
const AUTH0_CALLBACK_PARAMS = ["code", "state"];

const currentPath = () => {
  const params = new URLSearchParams(window.location.search);
  AUTH0_CALLBACK_PARAMS.forEach((param) => params.delete(param));
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ""}`;
};

export const installSessionRecovery = (
  http: AxiosInstance,
  {
    auth0Client,
    onUnrecoverable,
    storage = getSessionStorage(),
    getReturnTo = currentPath,
  }: SessionRecoveryOptions
): (() => void) => {
  let isRedirecting = false;
  // Once the message is showing, later refusals don't keep asking Auth0 for tokens
  let hasGivenUp = false;
  let pendingFreshToken: Promise<string | null> | null = null;

  // A Patient Portal request carries a token from the same Auth0 issuer as the family's ID
  // token (prod uses a custom domain). BI Portal requests carry Microsoft tokens and are left alone.
  const isPortalRequest = async (config?: InternalAxiosRequestConfig) => {
    const requestIssuer = getTokenIssuer(getBearerToken(config));
    if (!requestIssuer) return false;
    const claims = await Promise.resolve()
      .then(() => auth0Client.getIdTokenClaims())
      .catch(() => undefined);
    return requestIssuer === claims?.iss;
  };

  // Several screens can be refused at once; they all share one fresh token.
  const getFreshToken = () => {
    if (!pendingFreshToken) {
      pendingFreshToken = withTimeout(
        auth0Client.getTokenSilently({ ignoreCache: true }),
        FRESH_TOKEN_TIMEOUT_MS
      )
        .catch(() => null)
        .finally(() => {
          pendingFreshToken = null;
        });
    }
    return pendingFreshToken;
  };

  const onRejected = async (error: AxiosError) => {
    const config = error.config as RecoverableConfig | undefined;
    if (error.response?.status !== UNAUTHORIZED || !config || !(await isPortalRequest(config))) {
      throw error;
    }
    if (isRedirecting || hasGivenUp) throw error;

    // Unverified families are handled by the verify-email page, not here.
    const user = await Promise.resolve()
      .then(() => auth0Client.getUser())
      .catch(() => undefined);
    if (user?.email_verified === false) throw error;

    if (!config[RETRIED_FLAG]) {
      const freshToken = await getFreshToken();
      if (freshToken) {
        config[RETRIED_FLAG] = true;
        config.headers.Authorization = `Bearer ${freshToken}`;
        return http.request(config);
      }
    }

    // Another refused request may have started sign-in while this one waited
    if (isRedirecting) throw error;
    if (!readMarker(storage) && writeMarker(storage)) {
      isRedirecting = true;
      try {
        await auth0Client.loginWithRedirect({ appState: { targetUrl: getReturnTo() } });
        throw error;
      } catch (redirectError) {
        if (redirectError === error) throw error;
        isRedirecting = false;
      }
    }

    hasGivenUp = true;
    onUnrecoverable();
    throw error;
  };

  // Pressing Back on the Auth0 page can restore this page from the back/forward cache
  // mid-redirect; let recovery run again.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) isRedirecting = false;
  };

  const interceptorId = http.interceptors.response.use(undefined, onRejected);
  window.addEventListener("pageshow", onPageShow);
  return () => {
    http.interceptors.response.eject(interceptorId);
    window.removeEventListener("pageshow", onPageShow);
  };
};
